import { useState } from 'react';
import { CheckCircle2, ClipboardCopy, Loader2, Lock, Unlock } from 'lucide-react';
import clsx from 'clsx';
import { ToolHeader } from '../components/ToolHeader';
import { CryptoPrivacyBanner } from '../components/CryptoPrivacyBanner';
import { aesDecrypt, aesEncrypt } from '../lib/aesGcm';

type Direction = 'encrypt' | 'decrypt';
type KeyMode = 'password' | 'raw';

const SAMPLE_PLAINTEXT = '这是要加密的明文 — Hello, AES-GCM!';

function hexToBytes(hex: string): Uint8Array | null {
  const cleaned = hex.replace(/\s+/g, '').replace(/^0x/i, '');
  if (cleaned.length === 0) return null;
  if (!/^[0-9a-fA-F]+$/.test(cleaned)) return null;
  if (cleaned.length % 2 !== 0) return null;
  const out = new Uint8Array(cleaned.length / 2);
  for (let i = 0; i < cleaned.length; i += 2) {
    out[i / 2] = parseInt(cleaned.slice(i, i + 2), 16);
  }
  return out;
}

/**
 * `/tools/crypto/aes` — AES-GCM encrypt / decrypt.
 *
 * Two key modes:
 *   - password — PBKDF2-SHA-256 / 256 000 rounds → 256-bit key
 *   - raw      — caller supplies a 32-byte hex key
 *
 * The encrypted output is a single base64 envelope (versioned
 * header + salt + IV + ciphertext+tag) that the decrypt direction
 * understands without extra metadata.
 */
export default function AesTool(): JSX.Element {
  const [dir, setDir] = useState<Direction>('encrypt');
  const [keyMode, setKeyMode] = useState<KeyMode>('password');
  const [input, setInput] = useState<string>(SAMPLE_PLAINTEXT);
  const [password, setPassword] = useState('');
  const [rawKeyHex, setRawKeyHex] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const onRun = async (): Promise<void> => {
    setError(null);
    setOutput('');
    setBusy(true);
    try {
      if (dir === 'encrypt') {
        if (keyMode === 'password') {
          if (!password) throw new Error('请输入密码');
          const r = await aesEncrypt({ plaintext: input, password });
          setOutput(r.combined);
        } else {
          const rawKey = hexToBytes(rawKeyHex);
          if (!rawKey) throw new Error('请输入有效的 hex 密钥');
          if (rawKey.byteLength !== 32) {
            throw new Error(`密钥长度必须为 32 字节（当前 ${rawKey.byteLength}）`);
          }
          const r = await aesEncrypt({ plaintext: input, rawKey });
          setOutput(r.combined);
        }
      } else {
        if (keyMode === 'password') {
          if (!password) throw new Error('请输入密码');
          const r = await aesDecrypt(input, password);
          setOutput(r.plaintext);
        } else {
          const rawKey = hexToBytes(rawKeyHex);
          if (!rawKey) throw new Error('请输入有效的 hex 密钥');
          if (rawKey.byteLength !== 32) {
            throw new Error(`密钥长度必须为 32 字节（当前 ${rawKey.byteLength}）`);
          }
          const r = await aesDecrypt(input, undefined, rawKey);
          setOutput(r.plaintext);
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onCopy = async (): Promise<void> => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 900);
    } catch {
      /* ignore */
    }
  };

  const onSwitchDir = (d: Direction): void => {
    if (d === dir) return;
    setDir(d);
    setOutput('');
    setError(null);
  };

  const onGenRawKey = (): void => {
    const k = crypto.getRandomValues(new Uint8Array(32));
    setRawKeyHex(
      Array.from(k)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join(''),
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <ToolHeader
        toolId="aes"
        category="crypto"
        description="AES-256-GCM 加解密。密码模式走 PBKDF2-SHA-256 256 000 轮派生；raw-key 模式直接接受 32 字节 hex。所有运算走浏览器 SubtleCrypto。"
      />

      <CryptoPrivacyBanner />

      <section className="card-pad flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-wide text-ink-dim">
          方向
        </span>
        <div className="flex gap-1.5">
          <DirBtn
            active={dir === 'encrypt'}
            onClick={() => onSwitchDir('encrypt')}
          >
            <Lock className="h-3.5 w-3.5" />
            加密
          </DirBtn>
          <DirBtn
            active={dir === 'decrypt'}
            onClick={() => onSwitchDir('decrypt')}
          >
            <Unlock className="h-3.5 w-3.5" />
            解密
          </DirBtn>
        </div>
        <span className="ml-3 text-[11px] uppercase tracking-wide text-ink-dim">
          密钥
        </span>
        <div className="flex gap-1.5">
          <DirBtn
            active={keyMode === 'password'}
            onClick={() => setKeyMode('password')}
          >
            密码 (PBKDF2)
          </DirBtn>
          <DirBtn
            active={keyMode === 'raw'}
            onClick={() => setKeyMode('raw')}
          >
            raw 32B
          </DirBtn>
        </div>
      </section>

      <section className="card-pad flex flex-col gap-2">
        {keyMode === 'password' ? (
          <>
            <header className="text-[11px] uppercase tracking-wide text-ink-dim">
              密码
            </header>
            <input
              type="password"
              autoComplete="off"
              spellCheck={false}
              className="input font-mono text-[13px]"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="任意长度密码（PBKDF2-SHA-256 / 256k rounds）"
            />
          </>
        ) : (
          <>
            <header className="flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-wide text-ink-dim">
                raw 密钥（hex 64 字符）
              </span>
              <button
                type="button"
                onClick={onGenRawKey}
                className="btn-ghost px-2 py-0.5"
                title="生成 32 字节随机密钥"
              >
                生成
              </button>
            </header>
            <input
              type="text"
              autoComplete="off"
              spellCheck={false}
              className="input font-mono text-[13px]"
              value={rawKeyHex}
              onChange={(e) => setRawKeyHex(e.target.value)}
              placeholder="0123456789abcdef… (64 个 hex 字符)"
            />
          </>
        )}
      </section>

      <section className="card-pad flex flex-col gap-2">
        <header className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-ink-dim">
            {dir === 'encrypt' ? '明文' : '密文 (base64)'}
          </span>
          <span className="text-[11px] text-ink-dim">{input.length} 字符</span>
        </header>
        <textarea
          className="input min-h-[120px] resize-y font-mono text-[13px]"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          spellCheck={false}
          placeholder={
            dir === 'encrypt'
              ? '要加密的明文（任意 UTF-8）'
              : '密文 base64（来自此工具的加密输出）'
          }
        />
        <div>
          <button
            type="button"
            onClick={onRun}
            disabled={busy || !input}
            className="btn"
          >
            {busy ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                处理中…
              </>
            ) : dir === 'encrypt' ? (
              <>
                <Lock className="h-3.5 w-3.5" />
                加密
              </>
            ) : (
              <>
                <Unlock className="h-3.5 w-3.5" />
                解密
              </>
            )}
          </button>
        </div>
      </section>

      {error && (
        <div className="card-pad border-red-500/30 bg-red-500/5 text-[12px] text-red-500">
          {error}
        </div>
      )}

      <section className="card-pad flex min-h-0 flex-1 flex-col gap-2">
        <header className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-ink-dim">
            {dir === 'encrypt' ? '密文 (base64)' : '明文'}
          </span>
          <button
            type="button"
            onClick={onCopy}
            disabled={!output}
            className="btn-ghost px-2 py-0.5"
            title="复制"
          >
            {copied ? (
              <>
                <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                已复制
              </>
            ) : (
              <>
                <ClipboardCopy className="h-3 w-3" />
                复制
              </>
            )}
          </button>
        </header>
        <textarea
          className="input flex-1 resize-none font-mono text-[13px]"
          value={output}
          readOnly
          spellCheck={false}
          placeholder={busy ? '处理中…' : '点击按钮后在这里显示结果'}
        />
      </section>
    </div>
  );
}

function DirBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[12px] transition',
        active
          ? 'border-accent bg-accent/10 text-accent'
          : 'border-line text-ink-muted hover:border-accent/40 hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}
